import React, { useState, useEffect } from 'react'
import { Sheet } from 'components/ui/sheet'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Button } from 'components/ui/button'
import { X } from 'lucide-react'

const DETAIL_TYPE_OPTIONS = [
	{ value: 'Number', label: 'Number' },
	{ value: 'Currency', label: 'Currency' },
	{ value: 'Date', label: 'Date' },
]

interface DetailEditSheetProps {
	open: boolean
	onClose: () => void
	record: any
	onSave: (values: any, resetForm: () => void) => void
}

const DetailEditSheet = ({ open, onClose, record, onSave }: DetailEditSheetProps) => {
	const isEditMode = record && record._id && record._id !== 0

	const [detailLabel, setDetailLabel] = useState('')
	const [detailType, setDetailType] = useState('')
	const [detail, setDetail] = useState('')
	const [description, setDescription] = useState('')
	const [errors, setErrors] = useState<{ detailLabel?: string; detailType?: string }>({})

	useEffect(() => {
		if (open && record) {
			setDetailLabel(record.detailLabel || '')
			setDetailType(record.detailType || '')
			setDetail(record.detail || '')
			setDescription(record.description || '')
			setErrors({})
		}
	}, [open, record])

	const validate = () => {
		const newErrors: { detailLabel?: string; detailType?: string } = {}
		if (!detailLabel.trim()) newErrors.detailLabel = 'This field is required.'
		if (!detailType) newErrors.detailType = 'This field is required.'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const resetForm = () => {
		setDetailLabel('')
		setDetailType('')
		setDetail('')
		setDescription('')
		setErrors({})
	}

	const handleSave = () => {
		if (!validate()) return

		const values = {
			...(isEditMode ? record : {}),
			_id: isEditMode ? record._id : 0,
			detailLabel,
			detailType,
			detail,
			description,
		}

		onSave(values, resetForm)
	}

	const formatDateForInput = (value: string) => {
		if (!value) return ''
		try {
			const d = new Date(value)
			if (isNaN(d.getTime())) return ''
			return d.toISOString().split('T')[0]
		} catch {
			return ''
		}
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[400px]">
			<div className="flex flex-col h-full">
				{/* Header */}
				<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
					<h3 className="text-sm font-semibold text-gray-800">
						{isEditMode ? 'Update Detail' : 'New Detail'}
					</h3>
					<button
						onClick={onClose}
						className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors p-1"
					>
						<X className="w-4 h-4" />
					</button>
				</div>

				{/* Form */}
				<div className="flex-1 overflow-y-auto px-5 py-4">
					<div className="flex flex-col gap-4">
						{/* Detail Label */}
						<div className="flex flex-col gap-1.5">
							<Label className="text-xs font-medium text-gray-700">
								Detail Label <span className="text-red-500">*</span>
							</Label>
							<Input
								value={detailLabel}
								onChange={e => setDetailLabel(e.target.value)}
								className={`h-8 text-xs ${errors.detailLabel ? 'border-red-500' : ''}`}
								placeholder="e.g., Square Footage"
							/>
							{errors.detailLabel && (
								<span className="text-red-500 text-[0.7rem]">{errors.detailLabel}</span>
							)}
						</div>

						{/* Detail Type */}
						<div className="flex flex-col gap-1.5">
							<Label className="text-xs font-medium text-gray-700">
								Detail Type <span className="text-red-500">*</span>
							</Label>
							<Select
								value={detailType}
								onChange={e => {
									setDetailType(e.target.value)
									setDetail('')
								}}
								className={`h-8 text-xs ${errors.detailType ? 'border-red-500' : ''}`}
							>
								<option value="">Select a type...</option>
								{DETAIL_TYPE_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
							{errors.detailType && (
								<span className="text-red-500 text-[0.7rem]">{errors.detailType}</span>
							)}
						</div>

						{/* Value / Date */}
						{detailType && (
							<div className="flex flex-col gap-1.5">
								<Label className="text-xs font-medium text-gray-700">
									{detailType === 'Date' ? 'Initial Date' : 'Initial Value'}
								</Label>
								{detailType === 'Date' ? (
									<Input
										type="date"
										value={formatDateForInput(detail)}
										onChange={e => setDetail(e.target.value)}
										className="h-8 text-xs"
									/>
								) : (
									<Input
										value={detail}
										onChange={e => setDetail(e.target.value)}
										className="h-8 text-xs"
										placeholder={detailType === 'Currency' ? 'e.g., 150000' : 'e.g., 1500'}
									/>
								)}
							</div>
						)}

						{/* Note */}
						<div className="flex flex-col gap-1.5">
							<Label className="text-xs font-medium text-gray-700">Note</Label>
							<Input
								value={description}
								onChange={e => setDescription(e.target.value)}
								className="h-8 text-xs"
								placeholder="Optional note or description"
							/>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSave}>
						{isEditMode ? 'Update' : 'Create'}
					</Button>
				</div>
			</div>
		</Sheet>
	)
}

export default DetailEditSheet
